import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import React, { useMemo } from 'react';
import {
  formatTimeXAxisText,
  getChartPosition,
} from '@/pages/opinion/utils/chart';
import { echartsContent } from '@/pages/opinion/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { chartAllColors } from '@/pages/opinion/constants';
import { percentageFormat, reviewsFormat } from '@/pages/opinion/utils/value';
import { dealwithTitle } from '../../utils';

export default function LineEchart({ filter, data, height = 300 }: any) {
  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    const legends: { name: string; itemStyle: { color: string } }[] = [];
    let series: any = [];
    let xAxis: string[] = [];

    const obj: any = {};
    data.forEach((item: { name: any }) => {
      const key = item.name;
      if (!obj[key]) obj[key] = [];

      obj[key].push(item);
    });
    ['0-5', '5-10', '10-20', '20-50', '50-80', '80-100', '100+'].forEach(
      (key: string, index: number) => {
        const element = obj?.[key];
        if (!element) return;
        const color = chartAllColors[index];
        series.push({
          name: key,
          type: 'line',
          itemStyle: { color },
          data: element.map((item: any) => ({ ...item, value: item.count })),
        });
        legends.push({ name: key, itemStyle: { color } });
        if (xAxis.length === 0) {
          xAxis = element.map((item: any) => item.time);
        }
      },
    );

    let currentOptions = {
      tooltip: {
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) =>
          getChartPosition(pos, params, dom, rect, size, 50, 15, 'bottom', 3),
        appendToBody: false,
        confine: true,
        formatter: (params: any) => {
          let letTitle = listTimeTitleFunc(
            params[0].axisValue,
            filter.date_type || 'daily',
          );

          return formatChartParamsDate({
            params,
            beforeSort: true,
            listTitleFunc: (params) => dealwithTitle(params.seriesName),
            recordDealwith: (rows, ele) => {
              rows['share'] = percentageFormat(ele?.data?.rate * 100);
              return rows;
            },
            dealwithRecords: (records) => {
              records.sort(
                (a, b) => (b?.originValue || 0) - (a?.originValue || 0),
              );
            },
            valueFormat: (value) => reviewsFormat(value),
            tablesConfigFunc: (records: Array<any>) => {
              return [
                {
                  showHeader: true,
                  records,
                  columns: [
                    {
                      key: 'text',
                      header: letTitle,
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Reviews' }),
                    },
                    {
                      key: 'share',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Share-1' }),
                    },
                  ],
                },
              ];
            },
          });
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter?.date_type),
      ),
      grid: {
        top: 10,
        containLabel: true,
      },
      legend: {
        data: legends,
        formatter: (name: string) => {
          return dealwithTitle(name);
        },
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: xAxis,
        axisLabel: {
          showMaxLabel: true,
          formatter: (value: string) =>
            formatTimeXAxisText(value, filter?.date_type),
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return echartsContent(value);
            },
          },
        },
      ],
      series: series,
    };

    return currentOptions;
  }, [formatMessage, filter]);

  const noData = useMemo(() => {
    return !data.some((item: any) => item.count > 0 || item.rate > 0);
  }, [data]);

  return (
    <EChartCommon
      options={options}
      height={height}
      empty={noData}
      optionsProps={{ type: 'line' }}
    ></EChartCommon>
  );
}
